import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Modal from "../Modal";
import { audiencesService } from "../../services/audiences";
import { patternsService } from "../../services/patterns";
import { getPlatformIcon, getPlatformColor, getCreatorProfileUrl } from "../../utils/platforms";
import { formatNumber, patternLabelInSidebar } from "../patterns/utils";
import OrbitalLoader from "../common/OrbitalLoader";
import HeroIcon from "../common/HeroIcon";
import CreatorDailyBars, { CREATOR_DAILY_BARS_DEFAULT_METRIC } from "../audience/CreatorDailyBars";
import SampleVideoRow from "../patterns/SampleVideoRow";
import { parsePaginatedResponse } from "../../utils/pagination";

const VIDEO_SORT_OPTIONS = [
  { value: "views", label: "Views" },
  { value: "likes", label: "Likes" },
  { value: "comments", label: "Comments" },
  { value: "recent", label: "Recent" },
];

const videoSortValue = (row, key) => {
  if (key === "recent") {
    const t = row?.video?.uploaded_at;
    return t ? new Date(t).getTime() : 0;
  }
  if (key === "views") return row?.video?.count_views ?? -Infinity;
  if (key === "likes") return row?.video?.count_likes ?? -Infinity;
  if (key === "comments") return row?.video?.count_comments ?? -Infinity;
  return -Infinity;
};

// Stat card component
const StatCard = ({ label, value, icon }) => (
  <div className="bg-theme-secondary rounded-lg border border-theme-secondary p-4">
    <div className="flex items-center gap-2 mb-1">
      {icon && <div className="text-theme-primary opacity-70">{icon}</div>}
      <span className="text-xs text-theme-text opacity-70 uppercase tracking-wider">{label}</span>
    </div>
    <div className="text-2xl font-bold text-theme-text">{value}</div>
  </div>
);

const CreatorDetailModal = ({ audience, creatorHash, baseUrl, pageCreators = [] }) => {
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [videos, setVideos] = useState([]);
  const [loadingVideos, setLoadingVideos] = useState(true);
  const [imageError, setImageError] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [videoSortBy, setVideoSortBy] = useState("views");
  const [patternCategories, setPatternCategories] = useState([]);
  const [loadingPatternTypes, setLoadingPatternTypes] = useState(false);

  const creator = item?.creator;
  const platformIcon = getPlatformIcon(creator?.platform);
  const profileUrl = getCreatorProfileUrl(creator?.platform, creator?.platform_id);
  const platformColor = getPlatformColor(creator?.platform);

  const sortedVideos = useMemo(() => {
    const rows = [...videos];
    rows.sort((a, b) => videoSortValue(b, videoSortBy) - videoSortValue(a, videoSortBy));
    return rows;
  }, [videos, videoSortBy]);

  const patternCategoriesWithTypes = useMemo(
    () =>
      patternCategories
        .map((cat) => ({
          ...cat,
          pattern_types: (cat.pattern_types || []).filter((pt) => pt.handler),
        }))
        .filter((cat) => cat.pattern_types.length > 0),
    [patternCategories]
  );

  useEffect(() => {
    if (!audience?.hash || !creatorHash) {
      setLoading(false);
      return;
    }
    const seed = pageCreators.find((c) => c.hash === creatorHash || c.creator?.hash === creatorHash);
    setLoadError(false);
    if (seed) {
      setItem(seed);
      setLoading(false);
    } else {
      setItem(null);
      setLoading(true);
    }
    audiencesService.getAudienceCreatorByCreatorHash(
      audience.hash,
      creatorHash,
      (response) => {
        setItem(response.data);
        setLoading(false);
      },
      () => {
        setLoadError(!seed);
        setLoading(false);
      }
    );
  }, [audience?.hash, creatorHash, pageCreators]);

  useEffect(() => {
    setActiveTab("overview");
  }, [creatorHash]);

  useEffect(() => {
    if (!audience?.hash) return;
    setLoadingPatternTypes(true);
    patternsService.getPatternCategories(
      (response) => {
        const data = response.data?.categories ?? response.data ?? [];
        setPatternCategories(Array.isArray(data) ? data : []);
        setLoadingPatternTypes(false);
      },
      () => setLoadingPatternTypes(false)
    );
  }, [audience?.hash]);

  useEffect(() => {
    if (!item || !audience?.hash || !creator?.hash) return;

    setLoadingVideos(true);
    audiencesService.getAudienceVideos(
      {
        audience_hash: audience.hash,
        creator_hash: creator.hash,
        page_size: 1000,
      },
      (response) => {
        const { results } = parsePaginatedResponse(response);
        setVideos(results);
        setLoadingVideos(false);
      },
      () => {
        setLoadingVideos(false);
      }
    );
  }, [item, audience?.hash, creator?.hash]);

  const handleClose = () => {
    navigate(baseUrl);
  };

  if (!creatorHash) return null;

  if (loading && !item) {
    return (
      <Modal onClose={handleClose} maxWidth="max-w-5xl">
        <div className="flex items-center justify-center min-h-[200px]">
          <OrbitalLoader size={64} />
        </div>
      </Modal>
    );
  }

  if (!item) {
    return (
      <Modal onClose={handleClose} maxWidth="max-w-5xl">
        <div className="py-8 text-center text-theme-text">
          <p className="opacity-80">
            {loadError ? "Creator not found or you don't have access." : "No data."}
          </p>
        </div>
      </Modal>
    );
  }

  const listMetrics = item.list_metrics || {};
  const totalShortFormVideos = listMetrics.video_count ?? 0;
  const processedShortFormVideos = listMetrics.processed_video_count ?? 0;
  const dailySeries = listMetrics.daily || [];
  const monthlySeries = listMetrics.monthly || [];
  const rollupEngagement =
    listMetrics.engagement_rate != null && Number.isFinite(Number(listMetrics.engagement_rate))
      ? Number(listMetrics.engagement_rate)
      : null;

  const hasRichData = (creator?.name && creator?.name !== creator?.platform_id) || creator?.profile_image_url;
  const sourceLabel = item.source === "manual" ? "Manual roster" : "Category match";
  const handleLabel = creator?.platform_id ? `@${String(creator.platform_id).replace(/^@/, "")}` : "";

  const tabBtn = (id, label) => (
    <button
      type="button"
      onClick={() => setActiveTab(id)}
      className={`px-3 py-2 text-sm font-medium rounded-md border transition-colors ${
        activeTab === id
          ? "border-theme-secondary bg-theme-active-nav text-theme-text"
          : "border-transparent text-theme-text opacity-70 hover:opacity-100 hover:bg-theme-main"
      }`}
    >
      {label}
    </button>
  );

  return (
    <Modal onClose={handleClose} maxWidth="max-w-5xl">
      <div className="flex flex-col gap-4 max-h-[85vh] overflow-y-auto">
        <div className="flex items-start justify-between gap-3 flex-shrink-0">
          <div className="flex items-center gap-4 min-w-0 flex-1">
            {hasRichData && creator?.profile_image_url && !imageError ? (
              <img
                src={creator.profile_image_url}
                alt={creator.name || creator.platform_id}
                className="w-16 h-16 rounded-full object-cover flex-shrink-0"
                onError={() => setImageError(true)}
              />
            ) : (
              <div className="w-16 h-16 bg-theme-secondary rounded-full flex items-center justify-center text-theme-text opacity-60 flex-shrink-0">
                {platformIcon ? (
                  <div className="w-8 h-8">
                    {React.cloneElement(platformIcon, { className: "w-8 h-8" })}
                  </div>
                ) : (
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                )}
              </div>
            )}
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-xl font-bold text-theme-text truncate">
                  {creator?.name || creator?.platform_id || "Creator"}
                </h3>
                {creator?.verified && (
                  <svg className="w-5 h-5 text-theme-primary flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
                <span className="text-xs px-2 py-0.5 rounded border border-theme-secondary text-theme-text opacity-80">
                  {sourceLabel}
                </span>
                {item.category_name ? (
                  <span className="text-xs text-theme-text opacity-60 truncate max-w-[200px]" title={item.category_name}>
                    {item.category_name}
                  </span>
                ) : null}
              </div>
              {creator?.description && (
                <p className="text-sm text-theme-text opacity-70 mt-1 line-clamp-2 max-w-lg">
                  {creator.description}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0 self-start">
            {handleLabel ? (
              profileUrl ? (
                <a
                  href={profileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-semibold text-white hover:opacity-90 transition-opacity max-w-[220px] ${
                    !platformColor ? "bg-theme-primary" : ""
                  }`}
                  style={platformColor ? { backgroundColor: platformColor } : undefined}
                >
                  {platformIcon && (
                    <span className="w-4 h-4 shrink-0 [&>svg]:w-4 [&>svg]:h-4 opacity-95">
                      {React.cloneElement(platformIcon, { className: "w-4 h-4" })}
                    </span>
                  )}
                  <span className="truncate">{handleLabel}</span>
                </a>
              ) : (
                <span className="text-sm text-theme-text opacity-70 truncate max-w-[200px]" title={handleLabel}>
                  {handleLabel}
                </span>
              )
            ) : null}
            <button
              onClick={handleClose}
              className="text-theme-text opacity-70 hover:opacity-100 p-1 flex-shrink-0"
              aria-label="Close"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {loadError && (
          <p className="text-xs text-theme-text opacity-60 border border-theme-secondary rounded-md px-3 py-2 bg-theme-main">
            Detail request failed. Showing cached list data where available.
          </p>
        )}

        <div className="flex flex-wrap gap-1 border-b border-theme-secondary pb-2">
          {tabBtn("overview", "Overview")}
          {tabBtn("videos", "Videos")}
          {tabBtn("patterns", "Patterns")}
        </div>

        {activeTab === "overview" && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <StatCard
                label="Followers"
                value={formatNumber(creator?.subscribers || creator?.follower_count || 0)}
                icon={
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                }
              />
              <StatCard
                label="Processed videos"
                value={`${processedShortFormVideos}/${totalShortFormVideos}`}
                icon={
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                    />
                  </svg>
                }
              />
              <StatCard
                label="Avg views / video"
                value={listMetrics.avg_views != null ? formatNumber(listMetrics.avg_views) : "—"}
                icon={
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                    />
                  </svg>
                }
              />
              <StatCard
                label="Avg likes / video"
                value={listMetrics.avg_likes != null ? formatNumber(listMetrics.avg_likes) : "—"}
                icon={
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                    />
                  </svg>
                }
              />
              <StatCard
                label="Avg comments / video"
                value={listMetrics.avg_comments != null ? formatNumber(listMetrics.avg_comments) : "—"}
                icon={
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                    />
                  </svg>
                }
              />
              <StatCard
                label="Avg engagement / video"
                value={rollupEngagement != null ? `${rollupEngagement.toFixed(2)}%` : "—"}
                icon={
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                    />
                  </svg>
                }
              />
            </div>

            <div className="bg-theme-secondary rounded-lg border border-theme-secondary p-4">
              <CreatorDailyBars
                key={creatorHash}
                daily={dailySeries}
                monthly={monthlySeries}
                heightClass="h-20"
                defaultMetric={CREATOR_DAILY_BARS_DEFAULT_METRIC}
              />
            </div>
          </div>
        )}

        {activeTab === "patterns" && (
          <div className="space-y-4">
            {loadingPatternTypes ? (
              <div className="flex justify-center py-12">
                <OrbitalLoader size={52} />
              </div>
            ) : patternCategoriesWithTypes.length === 0 ? (
              <p className="text-sm text-theme-text opacity-60 text-center py-8">No pattern types available.</p>
            ) : (
              <div className="space-y-5">
                {patternCategoriesWithTypes.map((cat) => (
                  <section key={cat.id} aria-labelledby={`creator-modal-patterns-${cat.id}`}>
                    <h4
                      id={`creator-modal-patterns-${cat.id}`}
                      className="text-xs font-medium text-theme-text opacity-60 uppercase tracking-wider mb-2"
                    >
                      {cat.label}
                    </h4>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-1">
                      {cat.pattern_types.map((pt) => (
                        <button
                          key={pt.id}
                          type="button"
                          onClick={() => {
                            navigate(
                              `/audience/${audience.hash}/patterns/${encodeURIComponent(pt.id)}/?creator=${encodeURIComponent(
                                creator.hash
                              )}&uploaded=all`
                            );
                          }}
                          className="flex items-center gap-2 w-full text-left px-3 py-2 rounded-md text-sm text-theme-text bg-theme-secondary hover:opacity-90 transition-opacity min-w-0"
                        >
                          {pt.icon ? (
                            <HeroIcon name={pt.icon} className="w-4 h-4 flex-shrink-0 text-theme-primary opacity-80" />
                          ) : (
                            <span className="w-4 h-4 flex-shrink-0 rounded bg-theme-main opacity-40" aria-hidden />
                          )}
                          <span className="flex-1 min-w-0 truncate">{patternLabelInSidebar(pt.label, cat.label)}</span>
                          {pt.smart && (
                            <span className="flex-shrink-0" title="AI grouping" aria-hidden>
                              <HeroIcon name="brain" className="w-3.5 h-3.5 text-theme-text opacity-45" />
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  </section>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === "videos" && (
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <h4 className="text-sm font-semibold text-theme-text">
                Processed videos ({processedShortFormVideos}/{totalShortFormVideos})
              </h4>
              <div className="flex items-center gap-2">
                <span className="text-xs text-theme-text opacity-60">Sort</span>
                <select
                  value={videoSortBy}
                  onChange={(e) => setVideoSortBy(e.target.value)}
                  className="px-3 py-1.5 text-sm rounded-md border border-theme-secondary bg-theme-main text-theme-text focus:outline-none focus:ring-2 focus:ring-theme-primary"
                  aria-label="Sort videos"
                >
                  {VIDEO_SORT_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {loadingVideos ? (
              <div className="text-center py-8">
                <OrbitalLoader size={52} text="Loading videos..." />
              </div>
            ) : sortedVideos.length > 0 ? (
              <div className="space-y-4">
                {sortedVideos.map((row) => (
                  <SampleVideoRow
                    key={row.hash || row.video?.id}
                    video={row.video}
                    creator={row.creator}
                    creatorViewsOutlier={row.creator_views_outlier}
                    creatorLikesOutlier={row.creator_likes_outlier}
                    creatorCommentsOutlier={row.creator_comments_outlier}
                    content={row.content ?? {}}
                    formatNumber={formatNumber}
                    videoDetailUrl={`/audience/${audience.hash}/videos/${row.hash}/`}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-8 bg-theme-secondary rounded-lg border border-theme-secondary">
                <p className="text-sm text-theme-text opacity-60">No videos for this creator in this audience.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default CreatorDetailModal;
